import React, { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { filmApi } from '../../services/api/film';
import { takeWorkflowHandoff } from '../../services/api/workflowReadHandoff';
import { AK } from './workflowAssetsPreload';
import type { FilmPlan } from '../../types/film';
import { Ctx, pickPlanId, useFilmPlan, useFilmPlanOptional } from './filmPlanContextBase';

export { useFilmPlan, useFilmPlanOptional };

/** `initialPlan` (261029 P1 B2a): plan hạt giống của CHÍNH `initialPlanId` — mở PlanGate ngay; `getPlan` vẫn chạy và thay bản đầy đủ. Khác id ⇒ bỏ qua. */
export const FilmPlanProvider: React.FC<{ storyId: string; initialPlanId?: string; initialPlan?: FilmPlan | null; children: React.ReactNode }> = ({ storyId, initialPlanId, initialPlan, children }) => {
  const [plans, setPlans] = useState<FilmPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [planId, setPlanId] = useState<string | undefined>(initialPlanId);
  const [plan, setPlan] = useState<FilmPlan | null>(initialPlan && initialPlan.id === initialPlanId ? initialPlan : null);
  const [loadingPlan, setLoadingPlan] = useState(false);

  const reloadPlans = useCallback(async (opts?: { silent?: boolean; first?: boolean }) => {
    const silent = !!opts?.silent;
    if (!silent) setLoadingPlans(true);
    try { const p = await (opts?.first ? takeWorkflowHandoff(AK.plans(storyId), () => filmApi.listPlans(storyId)) : filmApi.listPlans(storyId)); setPlans(p); return p; }  // P3: chỉ lần nạp ĐẦU lấy từ chỗ giao
    catch (e: any) {
      // Poll im lặng thì cũng nuốt lỗi: một lượt poll trượt mạng không đáng nổ toast mỗi 5 giây.
      if (!silent) message.error(`Lỗi tải plans: ${e?.message}`);
      return [];
    }
    finally { if (!silent) setLoadingPlans(false); }
  }, [storyId]);

  const loadPlan = useCallback(async (id: string, first = false) => {
    setLoadingPlan(true);
    try { const fetchPlan = () => filmApi.getPlan(id); setPlan(await (first ? takeWorkflowHandoff(AK.plan(id), fetchPlan) : fetchPlan())); }   // P3: chỉ lần nạp ĐẦU lấy từ chỗ giao
    catch (e: any) { message.error(`Lỗi tải plan: ${e?.message}`); }
    finally { setLoadingPlan(false); }
  }, []);

  useEffect(() => { reloadPlans({ first: true }); }, [reloadPlans]);

  // Auto-chọn: current → preferred (initialPlanId) → plans[0]. Có preferred thì không rơi về plans[0] (F8).
  useEffect(() => {
    const next = pickPlanId({ current: planId, preferred: initialPlanId, plans });
    if (next !== undefined && next !== planId) setPlanId(next);
  }, [plans, planId, initialPlanId]);

  // Tải full plan mỗi khi đổi plan. Một lần getPlan / planId (P8e đo: 1 lời gọi, không lặp).
  useEffect(() => { if (planId) loadPlan(planId, true); else setPlan(null); }, [planId, loadPlan]);

  const selectPlan = useCallback((id?: string) => setPlanId(id), []);
  const reloadPlan = useCallback(() => { if (planId) loadPlan(planId); }, [planId, loadPlan]);

  return (
    <Ctx.Provider value={{ storyId, plans, loadingPlans, planId, plan, loadingPlan, selectPlan, reloadPlan, reloadPlans }}>
      {children}
    </Ctx.Provider>
  );
};
